import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SupplierSpendingChart = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState(6); // Number of months
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    fetchSpendingData();
  }, [timeRange]);

  const fetchSpendingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      // ✅ CORRECT: Call the dual-source analytics endpoint
      const response = await axios.get('/api/bills/analytics/spending', {
        params: { months: timeRange },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Analytics data received:', response.data);
      setAnalyticsData(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to load spending data:', err);
      setError('Failed to load spending data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Chart data builders
  const monthlyChartData = {
    labels: analyticsData?.monthly?.map(d => d.month) || [],
    datasets: [
      {
        label: 'Monthly Spending',
        data: analyticsData?.monthly?.map(d => d.amount) || [],
        backgroundColor: 'rgba(25, 118, 210, 0.7)',
        borderColor: 'rgba(25, 118, 210, 1)',
        borderWidth: 2
      }
    ]
  };

  const trendChartData = {
    labels: analyticsData?.monthly?.map(d => d.month) || [],
    datasets: [
      {
        label: 'Spending Trend',
        data: analyticsData?.monthly?.map(d => d.amount) || [],
        fill: true,
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderColor: 'rgba(76, 175, 80, 1)',
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(76, 175, 80, 1)'
      }
    ]
  };

  const topSuppliersChartData = {
    labels: analyticsData?.top_suppliers?.map(s => s.name) || [],
    datasets: [
      {
        label: 'Total Spending',
        data: analyticsData?.top_suppliers?.map(s => s.amount) || [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(199, 199, 199, 0.7)',
          'rgba(83, 102, 255, 0.7)',
          'rgba(255, 99, 255, 0.7)',
          'rgba(99, 255, 132, 0.7)'
        ],
        borderWidth: 0
      }
    ]
  };

  const categoryChartData = {
    labels: analyticsData?.categories?.map(c => c.category_name) || [],
    datasets: [
      {
        data: analyticsData?.categories?.map(c => c.amount) || [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(199, 199, 199, 0.8)',
          'rgba(83, 102, 255, 0.8)',
          'rgba(255, 159, 132, 0.8)',
          'rgba(132, 255, 159, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP'
            }).format(context.parsed.y || context.parsed);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '£' + value.toLocaleString();
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP'
            }).format(context.parsed);
            return `${label}: ${value}`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Supplier Spending Analytics</Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value={6}>Last 6 Months</MenuItem>
            <MenuItem value={12}>Last 12 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Source Info */}
      {analyticsData?.summary?.data_sources && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {analyticsData.summary.data_sources.note}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Monthly Trends" />
        <Tab label="Top Suppliers" />
        <Tab label="Category Breakdown" />
      </Tabs>

      {/* Monthly Spending */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Spending (Bar Chart)
                </Typography>
                <Box sx={{ height: 300 }}>
                  {analyticsData?.monthly?.length > 0 ? (
                    <Bar data={monthlyChartData} options={chartOptions} />
                  ) : (
                    <Typography>No data available</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Spending Trend (Line Chart)
                </Typography>
                <Box sx={{ height: 300 }}>
                  {analyticsData?.monthly?.length > 0 ? (
                    <Line data={trendChartData} options={chartOptions} />
                  ) : (
                    <Typography>No data available</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Summary Stats */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: 'primary.light' }}>
                  <CardContent>
                    <Typography color="primary.contrastText" variant="body2">
                      Total Spending ({timeRange} months)
                    </Typography>
                    <Typography color="primary.contrastText" variant="h4">
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(analyticsData?.summary?.total_spending || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: 'success.light' }}>
                  <CardContent>
                    <Typography color="success.contrastText" variant="body2">
                      Average Monthly
                    </Typography>
                    <Typography color="success.contrastText" variant="h4">
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(analyticsData?.summary?.average_monthly || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: 'warning.light' }}>
                  <CardContent>
                    <Typography color="warning.contrastText" variant="body2">
                      Peak Month
                    </Typography>
                    <Typography color="warning.contrastText" variant="h5">
                      {analyticsData?.summary?.peak_month || 'N/A'}
                    </Typography>
                    <Typography color="warning.contrastText" variant="body2">
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP'
                      }).format(analyticsData?.summary?.peak_amount || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Top Suppliers */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top 10 Suppliers by Total Spending
                </Typography>
                <Box sx={{ height: 400 }}>
                  {analyticsData?.top_suppliers?.length > 0 ? (
                    <Bar data={topSuppliersChartData} options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return new Intl.NumberFormat('en-GB', {
                                style: 'currency',
                                currency: 'GBP'
                              }).format(context.parsed.x);
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '£' + value.toLocaleString();
                            }
                          }
                        },
                        y: {
                          ticks: {
                            autoSkip: false
                          }
                        }
                      }
                    }} />
                  ) : (
                    <Typography>No supplier data available</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category Breakdown */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Spending by Category
                </Typography>
                <Box sx={{ height: 400 }}>
                  {analyticsData?.categories?.length > 0 ? (
                    <Doughnut data={categoryChartData} options={doughnutOptions} />
                  ) : (
                    <Typography>No category data available</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Details
                </Typography>
                <Box>
                  {analyticsData?.categories?.length > 0 ? (
                    analyticsData.categories.map((cat, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography>{cat.category_name}</Typography>
                        <Typography fontWeight="bold">
                          {new Intl.NumberFormat('en-GB', {
                            style: 'currency',
                            currency: 'GBP'
                          }).format(cat.amount)}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography>No category data available</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SupplierSpendingChart;